import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  MessageCircle, Send, Bot, User, Shield, ArrowLeft, Loader2,
  Sparkles, RefreshCw, AlertTriangle, CheckCircle2, Clock,
  ChevronRight, Ticket, Users, BarChart3, Settings,
  HelpCircle, Search, X, ExternalLink
} from 'lucide-react';

const SupportPage = () => {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [escalated, setEscalated] = useState(false);
  
  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('chat'); // chat, admin-sessions, admin-tickets, admin-stats
  const [adminSessions, setAdminSessions] = useState([]);
  const [adminTickets, setAdminTickets] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  // Check admin status
  useEffect(() => {
    if (user) {
      const adminEmail = 'mleroux@notariasanantonio.cl';
      setIsAdmin(user.email === adminEmail || user.is_admin);
    }
  }, [user]);

  // Load chat history on mount
  useEffect(() => {
    loadChatHistory();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadChatHistory = async () => {
    try {
      const res = await api.get('/support-ai/history');
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages);
        setSessionId(res.data.session_id);
        setEscalated(res.data.escalated);
      } else {
        // Add welcome message
        setMessages([{
          role: 'assistant',
          content: '¡Hola! Soy el Asistente de Soporte de NotaryOS. Estoy aquí para ayudarte con cualquier pregunta o problema que tengas con la plataforma.\n\n¿En qué puedo ayudarte hoy?',
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || loading) return;
    
    const userMessage = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setLoading(true);
    
    try {
      const res = await api.post('/support-ai/chat', {
        message: inputMessage,
        context: window.location.pathname
      });
      
      const assistantMessage = {
        role: 'assistant',
        content: res.data.response,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      setSessionId(res.data.session_id);
      
      if (res.data.escalated) {
        setEscalated(true);
        toast.info('Tu conversación ha sido marcada para revisión por un administrador');
      }
    } catch (error) {
      toast.error('Error al enviar mensaje');
      // Remove the user message if failed
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    try {
      await api.post('/support-ai/clear');
      setMessages([{
        role: 'assistant',
        content: '¡Hola! Soy el Asistente de Soporte de NotaryOS. Estoy aquí para ayudarte con cualquier pregunta o problema que tengas con la plataforma.\n\n¿En qué puedo ayudarte hoy?',
        timestamp: new Date().toISOString()
      }]);
      setSessionId(null);
      setEscalated(false);
      toast.success('Conversación reiniciada');
    } catch (error) {
      toast.error('Error al reiniciar conversación');
    }
  };

  const escalateToHuman = async () => {
    try {
      const res = await api.post('/support-ai/escalate');
      setEscalated(true);
      toast.success('Tu conversación ha sido escalada a soporte humano');
      
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Tu conversación ha sido escalada. Se ha creado el ticket #${res.data.ticket_id.slice(0, 8)}. Un administrador te responderá pronto.`,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error('Error al escalar conversación');
    }
  };

  // Admin functions
  const loadAdminData = async () => {
    if (!isAdmin) return;
    setLoadingAdmin(true);
    
    try {
      const [sessionsRes, ticketsRes, statsRes] = await Promise.all([
        api.get('/support-ai/admin/sessions?escalated_only=true'),
        api.get('/support/tickets'),
        api.get('/support-ai/admin/stats')
      ]);
      
      setAdminSessions(sessionsRes.data.sessions || []);
      setAdminTickets(ticketsRes.data.tickets || []);
      setAdminStats(statsRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoadingAdmin(false);
    }
  };

  useEffect(() => {
    if (isAdmin && activeTab.startsWith('admin')) {
      loadAdminData();
    }
  }, [isAdmin, activeTab]);

  const loadSessionDetail = async (sessionId) => {
    try {
      const res = await api.get(`/support-ai/admin/sessions/${sessionId}`);
      setSelectedSession(res.data);
    } catch (error) {
      toast.error('Error al cargar sesión');
    }
  };

  const sendAdminReply = async (resolve = false) => {
    if (!adminReply.trim() || !selectedSession) return;
    
    try {
      await api.post(`/support-ai/admin/sessions/${selectedSession.id}/reply`, {
        message: adminReply,
        resolve
      });
      
      toast.success(resolve ? 'Sesión resuelta' : 'Respuesta enviada');
      setAdminReply('');
      loadSessionDetail(selectedSession.id);
      loadAdminData();
    } catch (error) {
      toast.error('Error al enviar respuesta');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="min-h-screen mesh-gradient pb-20">
      {/* Header */}
      <header className="glass sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Centro de Soporte IA</h1>
                <p className="text-sm text-slate-400">Asistente inteligente 24/7</p>
              </div>
            </div>
            
            {isAdmin && (
              <div className="flex items-center space-x-2">
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full flex items-center">
                  <Shield className="w-3 h-3 mr-1" />
                  Admin
                </span>
              </div>
            )}
          </div>
          
          {/* Tabs */}
          {isAdmin && (
            <div className="mt-4 flex space-x-2 overflow-x-auto pb-2">
              <button
                onClick={() => setActiveTab('chat')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === 'chat' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                <span>Mi Chat</span>
              </button>
              <button
                onClick={() => setActiveTab('admin-sessions')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === 'admin-sessions' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <Users className="w-4 h-4" />
                <span>Sesiones Escaladas</span>
                {adminSessions.length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                    {adminSessions.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('admin-tickets')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === 'admin-tickets' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <Ticket className="w-4 h-4" />
                <span>Tickets</span>
              </button>
              <button
                onClick={() => setActiveTab('admin-stats')}
                className={`px-4 py-2 rounded-lg flex items-center space-x-2 whitespace-nowrap transition-colors ${
                  activeTab === 'admin-stats' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                <BarChart3 className="w-4 h-4" />
                <span>Estadísticas</span>
              </button>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* User Chat Tab */}
        {activeTab === 'chat' && (
          <div className="flex flex-col h-[calc(100vh-220px)]">
            {/* Status Bar */}
            {escalated && (
              <div className="mb-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-xl flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-300 text-sm">
                  Esta conversación ha sido escalada a soporte humano
                </span>
              </div>
            )}
            
            {/* Messages Container */}
            <div className="flex-1 overflow-y-auto space-y-4 mb-4 pr-2">
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl p-4 ${
                      msg.role === 'user'
                        ? 'bg-blue-600 text-white'
                        : msg.role === 'admin'
                        ? 'bg-red-500/20 border border-red-500/30'
                        : msg.role === 'system'
                        ? 'bg-yellow-500/20 border border-yellow-500/30'
                        : 'bg-slate-800 border border-slate-700'
                    }`}
                  >
                    {msg.role !== 'user' && (
                      <div className="flex items-center space-x-2 mb-2">
                        {msg.role === 'admin' ? (
                          <>
                            <Shield className="w-4 h-4 text-red-400" />
                            <span className="text-xs text-red-400 font-medium">Administrador</span>
                          </>
                        ) : msg.role === 'system' ? (
                          <>
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            <span className="text-xs text-yellow-400 font-medium">Sistema</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 text-purple-400" />
                            <span className="text-xs text-purple-400 font-medium">Asistente IA</span>
                          </>
                        )}
                      </div>
                    )}
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                    <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-200' : 'text-slate-500'}`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-4">
                    <div className="flex items-center space-x-2">
                      <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
                      <span className="text-sm text-slate-400">Pensando...</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Input Area */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-4">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Escribe tu pregunta..."
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 focus:border-blue-500 transition-colors"
                  disabled={loading}
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !inputMessage.trim()}
                  className="px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              
              {/* Quick Actions */}
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                <div className="flex space-x-2">
                  <button
                    onClick={clearChat}
                    className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors flex items-center space-x-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    <span>Nueva conversación</span>
                  </button>
                  {!escalated && messages.length > 2 && (
                    <button
                      onClick={escalateToHuman}
                      className="px-3 py-1.5 text-xs bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 rounded-lg transition-colors flex items-center space-x-1"
                    >
                      <User className="w-3 h-3" />
                      <span>Hablar con humano</span>
                    </button>
                  )}
                </div>
                <p className="text-xs text-slate-500">
                  Powered by Claude AI
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Admin Sessions Tab */}
        {activeTab === 'admin-sessions' && isAdmin && (
          <div>
            {selectedSession ? (
              // Session Detail
              <div>
                <button
                  onClick={() => setSelectedSession(null)}
                  className="flex items-center space-x-2 text-slate-400 hover:text-white mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Volver a sesiones</span>
                </button>
                
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-semibold">{selectedSession.user_email || 'Usuario'}</h3>
                      <p className="text-sm text-slate-400">
                        Sesión: {selectedSession.id.slice(0, 8)}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      selectedSession.status === 'resolved'
                        ? 'bg-green-500/20 text-green-400'
                        : selectedSession.escalated
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {selectedSession.status === 'resolved' ? 'Resuelto' : selectedSession.escalated ? 'Escalado' : 'Activo'}
                    </span>
                  </div>
                  
                  {/* Messages */}
                  <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                    {selectedSession.messages?.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`p-3 rounded-lg ${
                          msg.role === 'user'
                            ? 'bg-blue-500/10 border border-blue-500/20'
                            : msg.role === 'admin'
                            ? 'bg-red-500/10 border border-red-500/20'
                            : 'bg-slate-900/50'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium">
                            {msg.role === 'user' ? 'Usuario' : msg.role === 'admin' ? 'Admin' : 'IA'}
                          </span>
                          <span className="text-xs text-slate-500">{formatTime(msg.timestamp)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Admin Reply */}
                  {selectedSession.status !== 'resolved' && (
                    <div className="flex flex-col space-y-3">
                      <textarea
                        value={adminReply}
                        onChange={(e) => setAdminReply(e.target.value)}
                        placeholder="Escribe una respuesta al usuario..."
                        className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg focus:border-red-500 transition-colors resize-none"
                        rows={3}
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={() => sendAdminReply(false)}
                          disabled={!adminReply.trim()}
                          className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-lg transition-colors"
                        >
                          Enviar Respuesta
                        </button>
                        <button
                          onClick={() => sendAdminReply(true)}
                          disabled={!adminReply.trim()}
                          className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded-lg transition-colors flex items-center space-x-2"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Resolver</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Sessions List
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold">Sesiones Escaladas</h2>
                  <button
                    onClick={loadAdminData}
                    className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  >
                    <RefreshCw className={`w-5 h-5 ${loadingAdmin ? 'animate-spin' : ''}`} />
                  </button>
                </div>
                
                {loadingAdmin ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : adminSessions.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
                    <p className="text-slate-400">No hay sesiones escaladas pendientes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {adminSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => loadSessionDetail(session.id)}
                        className="w-full bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-left hover:bg-slate-700/30 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{session.user_email || 'Usuario'}</p>
                            <p className="text-sm text-slate-400">
                              {session.messages?.length || 0} mensajes
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {formatDate(session.updated_at)} {formatTime(session.updated_at)}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Admin Tickets Tab */}
        {activeTab === 'admin-tickets' && isAdmin && (
          <div>
            <h2 className="text-xl font-semibold mb-6">Tickets de Soporte</h2>
            
            {loadingAdmin ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : adminTickets.length === 0 ? (
              <div className="text-center py-12">
                <Ticket className="w-16 h-16 mx-auto text-slate-600 mb-4" />
                <p className="text-slate-400">No hay tickets pendientes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {adminTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className="bg-slate-800/50 border border-slate-700 rounded-xl p-4"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{ticket.subject}</p>
                        <p className="text-sm text-slate-400">{ticket.user_email}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        ticket.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                        ticket.status === 'in_progress' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {ticket.status === 'open' ? 'Abierto' : 
                         ticket.status === 'in_progress' ? 'En Proceso' : 'Resuelto'}
                      </span>
                    </div>
                    <p className="text-sm text-slate-300 line-clamp-2">{ticket.description}</p>
                    <p className="text-xs text-slate-500 mt-2">
                      {formatDate(ticket.created_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Admin Stats Tab */}
        {activeTab === 'admin-stats' && isAdmin && (
          <div>
            <h2 className="text-xl font-semibold mb-6">Estadísticas de Soporte</h2>
            
            {loadingAdmin ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : adminStats ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-blue-400">{adminStats.total_sessions}</p>
                  <p className="text-sm text-slate-400 mt-1">Sesiones Totales</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-green-400">{adminStats.active_sessions}</p>
                  <p className="text-sm text-slate-400 mt-1">Sesiones Activas</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-red-400">{adminStats.escalated_sessions}</p>
                  <p className="text-sm text-slate-400 mt-1">Escaladas</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-purple-400">{adminStats.resolved_sessions}</p>
                  <p className="text-sm text-slate-400 mt-1">Resueltas</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-yellow-400">{adminStats.escalation_rate}%</p>
                  <p className="text-sm text-slate-400 mt-1">Tasa de Escalación</p>
                </div>
                <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                  <p className="text-3xl font-bold text-cyan-400">{adminStats.avg_messages_per_session}</p>
                  <p className="text-sm text-slate-400 mt-1">Msgs/Sesión</p>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
};

export default SupportPage;
